<template>
  <highcharts ref="PotsChart" :options="chartOptions"></highcharts>
</template>

<script lang="ts">
    import { defineComponent, ref } from "vue";
    import Highcharts from "highcharts";
    import { Chart } from "highcharts-vue";
    import "highcharts/modules/accessibility";
    import { SeriesColour } from "@/guiide/ts/charts/seriesColour";

    export default defineComponent({
      name: "PotsChart",
      components: {
        highcharts: Chart
      },
      setup() {
        const PotsChart = ref(null);

        return {
          PotsChart
        };
      },
      updated() {
        this.PotsChart.chart.reflow();
      },
      computed: {
        getPotsChartData() {
          const potsChartData = this.$store.state.potsSummaryData;

          const styledSeries = [];

          for (let i = 0; i < potsChartData.length; i++) {
            const color = SeriesColour.getColour(potsChartData[i].class);

            styledSeries.push({
                ...potsChartData[i],
                color
            });
          }

          return styledSeries;
        },
        chartOptions() {
          return {
            accessibility: {
                enabled: true,
                description: 'A chart displaying how the money in your various pots makes up the total amount for each year.',
                keyboardNavigation: {
                    enabled: true,
                },
                tooltip: {
                    enabled: true,
                },
                announceNewData: {
                    enabled: true,
                    minAnnounceInterval: 15000
                },
                point: {
                    descriptionFormatter: function (point) {
                        return `Year ${point.category}, value ${point.y} thousand pounds.`;
                    }
                }
            },
            chart: {
              backgroundColor: "white",
              borderColor: "lightgray",
              borderWidth: 0,
              borderRadius: 0,
              zoomType: "x",
              panning: { enabled: false },
              type: "column",
              marginBottom: 115
            },
            title: {
              text: "Pot Value (end of each year)",
              style: {
                color: "#5c5b5b",
                fontWeight: "600",
                fontSize: "14px"
              }
            },
            xAxis: {
              title: {
                text: "End of Retirement Year",
                style: {
                  color: "#5c5b5b",
                  fontWeight: "500",
                  fontSize: "12px"
                }
              },
              accessibility: {
                  description: 'End of Retirement Year.'
              },
              allowDecimals: false,
              tickInterval: 1,
              tickLength: 0
            },
            yAxis: {
              title: {
                text: null,
                rotation: 0,
                style: {
                  color: "#5c5b5b",
                  fontWeight: "500",
                  fontSize: "12px"
                }
              },
              accessibility: {
                  description: 'Cash amount (in thousands) allocated to each year.'
              },
              labels: {
                formatter: function() {
                  return (
                    "£" +
                    Highcharts.numberFormat(this.value / 1000, 0, ".", ",") +
                    "k"
                  );
                }
              },
              stackLabels: {
                enabled: false,
                style: {
                  fontWeight: "bold",
                  color: "gray"
                }
              }
            },
            legend: {
              verticalAlign: "bottom" as Highcharts.VerticalAlignValue,
              floating: false,
              backgroundColor: "white",
              borderColor: "LightGray",
              borderWidth: 0,
              borderRadius: 5,
              shadow: false,
              itemStyle: {
                color: "Grey",
                fontWeight: "lighter",
                fontSize: "12px"
              }
            },
            tooltip: {
              backgroundColor: "white",
              borderWidth: 1,
              borderRadius: 3,
              shadow: false,
              formatter: function() {
                return (
                  "<b>" +
                  "Retirement Year " +
                  this.x +
                  "</b><br/>" +
                  this.series.name +
                  ": " +
                  "\u00A3" +
                  Highcharts.numberFormat(this.y, 0, ".", ",") +
                  "<br/>" +
                  "Total at end of year: " +
                  "\u00A3" +
                  Highcharts.numberFormat(this.point.stackTotal, 0, ".", ",")
                );
              }
            },
            plotOptions: {
              column: {
                pointPadding: -0.1,
                borderWidth: 1,
                shadow: false,
                borderRadius: 0,
                stacking: "normal" as Highcharts.OptionsStackingValue,
                dataLabels: {
                  enabled: false,
                  color: "black"
                },
                pointStart: 1
              }
            },
            series: this.getPotsChartData,
            credits: {
                enabled: false
            },
            exporting: {
              enabled: false
            }
          };
        }
      }
    });
</script>

<style>
    .highcharts-container {
      width: 100% !important;
    }
</style>
